<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
      </div>
      <div class="columns">
        <div class="column">
          <b-field grouped>
            <b-field label="Start Date">
              <b-datepicker
                v-model="start"
                name="startdate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="Start date"
              />
            </b-field>
            <b-field label="End Date">
              <b-datepicker
                v-model="end"
                name="enddate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="End date"
              />
            </b-field>
            <b-field v-if="hasGroupPlants" label="Plant">
              <b-select v-model="selectedPlant" icon="industry" expanded>
                <option v-for="plant in groupPlants" :key="plant.id" :value="plant">
                  {{ plant.name }}
                </option>
              </b-select>
            </b-field>
            <b-field v-else label="Plant">
              <multiselect
                v-model="selectedPlant"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
          </b-field>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || selectedPlant === null"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
            <b-button
              class="left-right ml-3"
              type="is-success"
              icon-left="download"
              :disabled="!isLoaded"
              @click="() => {exportToExcel()}"
            >
              Export xlsx
            </b-button>
          </b-field>
          <b-field v-if="isLoaded" label="Hourly productivity targets" grouped>
            <b-field label="Manual target">
              {{ bonus.hourly_washing }}
            </b-field>
            <b-field label="Machine target">
              {{ bonus.hourly_machinewash }}
            </b-field>
            <b-field label="Crate repair target">
              {{ bonus.hourly_repair }}
            </b-field>
            <b-field label="Door repair target">
              {{ bonus.hourly_repair_door }}
            </b-field>
            <b-field label="Minimum Production Percentage">
              {{ bonus.under_production_target + '%' }}
            </b-field>
          </b-field>
          <b-field v-if="isLoaded" label="Bonus amounts" grouped>
            <b-field label="Attendance bonus">
              {{ bonus.attendance_amount }}
            </b-field>
            <b-field label="All targets met bonus">
              {{ bonus.all_targets_amount }}
            </b-field>
            <b-field label="Production target met bonus">
              {{ bonus.prod_target_amount }}
            </b-field>
            <b-field label="Quality and mininum target met bonus">
              {{ bonus.nc_target_amount }}
            </b-field>
            <b-field label="Minimum Production target met bonus">
              {{ bonus.under_production_amount }}
            </b-field>
          </b-field>
          <b-table
            v-if="isLoaded"
            :data="displayedEmployees"
            narrowed
            striped
            hoverable
          >
            <b-table-column v-slot="props" field="name" label="Employee" sortable>
              <strong>
                {{ props.row.name + (props.row.surname && props.row.surname.length > 0 ? ' ' + props.row.surname : '') }}
              </strong>
            </b-table-column>
            <b-table-column
              v-slot="props"
              field="absent_days"
              label="Absence"
              sortable
              numeric
            >
              {{ props.row.absent_days }}
            </b-table-column>
            <b-table-column
              v-slot="props"
              field="washing_productivity"
              label="Manual"
              sortable
              numeric
            >
              {{ props.row.washing_productivity.toFixed(1) }}
            </b-table-column>
            <b-table-column
              v-slot="props"
              field="machine_productivity"
              label="Machine"
              sortable
              numeric
            >
              {{ props.row.machine_productivity.toFixed(1) }}
            </b-table-column>
            <b-table-column
              v-slot="props"
              field="repair_productivity"
              label="Crate repair"
              sortable
              numeric
            >
              {{ props.row.repair_productivity.toFixed(1) }}
            </b-table-column>
            <b-table-column
              v-slot="props"
              field="door_productivity"
              label="Door repair"
              sortable
              numeric
            >
              {{ props.row.door_productivity.toFixed(1) }}
            </b-table-column>
            <b-table-column label="Forklift" numeric>
              {{ 'N/A' }}
            </b-table-column>
            <b-table-column v-slot="props" field="main_activity" label="Main" sortable>
              {{ props.row.main_activity }}
            </b-table-column>
            <b-table-column v-slot="props" label="Target Bonus" numeric>
              {{ targetBonus(props.row) }}
            </b-table-column>
            <b-table-column v-slot="props" label="Att Bonus" numeric>
              {{ attendanceBonus(props.row) }}
            </b-table-column>
            <template slot="empty">
              <div class="has-text-centered">
                No employee worked during this period
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import BonusManagement from '../../services/v3/bonus'
import EmployeeSchedule from '../../services/v3/employeeSchedule'
import CrateMovements from '../../services/v3/crateMovements'
import NCDeclaration from '../../services/v3/ncDeclaration'
import XLSX from 'xlsx'
import plantSelector from '@/mixins/plantSelector'
import { restoreConsultingPlant } from '@/helpers/plants'
import { dayKey } from '@/helpers/dates'
// import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'

export default {
  name: 'BonusCalculator',
  components: {
  },
  mixins: [plantSelector],
  data: function () {
    return {
      start: null,
      end: new Date(),
      savedStart: null,
      savedEnd: null,
      selectedPlant: restoreConsultingPlant(),
      isLoading: false,
      isLoaded: false,
      storedPlantId: null,
      bonus: {
        hourly_washing: 0,
        hourly_machinewash: 0,
        hourly_repair: 0,
        hourly_repair_door: 0,
        attendance_amount: 0,
        all_targets_amount: 0,
        prod_target_amount: 0,
        nc_target_amount: 0,
        under_production_amount: 0,
        under_production_target: 0
      },
      movements: [],
      employees: [],
      ncs: []
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    // only employees with hours on a non-absence activity during the period
    displayedEmployees () {
      return this.employees.filter(employee => employee.workedHours > 0)
    }
  },
  mounted: async function () {
    this.start = new Date()
    this.start.setDate(new Date().getDate() - 14)
    await this.loadPlants()
  },
  methods: {
    targetBonus (employee) {
      if (employee.production_target && employee.quality_target) {
        return this.bonus.all_targets_amount
      }
      if (employee.production_target) {
        return this.bonus.prod_target_amount
      }
      if (employee.quality_target && employee.lowproductionTarget) {
        return this.bonus.nc_target_amount
      }
      if (employee.lowproductionTarget) {
        return this.bonus.under_production_amount
      }
      return 0
    },
    attendanceBonus (employee) {
      return employee.absent_days <= 0 ? this.bonus.attendance_amount : 0
    },
    resetData () {
      this.storedPlantId = null
      this.bonus = {
        hourly_washing: 0,
        hourly_machinewash: 0,
        hourly_repair: 0,
        hourly_repair_door: 0,
        attendance_amount: 0,
        all_targets_amount: 0,
        prod_target_amount: 0,
        nc_target_amount: 0,
        under_production_amount: 0,
        under_production_target: 0
      }
      this.movements = []
      this.employees = []
      this.ncs = []
      this.savedStart = null
      this.savedEnd = null
    },
    async getData () {
      if (!this.selectedPlant || !this.selectedPlant.id) {
        this.$buefy.toast.open({ message: 'Please select a plant', duration: 3000, type: 'is-danger' })
        return
      }
      sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      this.isLoading = true
      try {
        this.savedStart = this.start.toLocaleDateString('en-CA')
        this.savedEnd = this.end.toLocaleDateString('en-CA')
        const quality = await NCDeclaration.getTable({
          start: this.start.toLocaleDateString('en-CA'),
          end: this.end.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id
        })
        quality.forEach((element) => {
          this.ncs.push({
            id: element.id,
            id_site: element.id_site,
            comment: element.comment,
            date: element.date ? dayKey(element.date) : null
          })
        })
        const data = await BonusManagement.get({
          siteId: this.selectedPlant.id
        })
        if (data.length > 0) {
          this.bonus = {
            id: data[0].id,
            siteId: this.selectedPlant.id,
            hourly_washing: data[0].hourly_washing,
            hourly_machinewash: data[0].hourly_machinewash,
            hourly_repair: data[0].hourly_repair,
            hourly_repair_door: data[0].hourly_repair_door,
            attendance_amount: data[0].attendance_amount,
            all_targets_amount: data[0].all_targets_amount,
            prod_target_amount: data[0].prod_target_amount,
            nc_target_amount: data[0].nc_target_amount,
            under_production_amount: data[0].under_production_amount,
            under_production_target: data[0].under_production_target
          }
          this.$buefy.toast.open({ message: 'Data loaded', duration: 3000, type: 'is-success' })
        } else {
          this.bonus = {
            siteId: this.selectedPlant.id,
            hourly_washing: 0,
            hourly_machinewash: 0,
            hourly_repair: 0,
            hourly_repair_door: 0,
            attendance_amount: 0,
            all_targets_amount: 0,
            prod_target_amount: 0,
            nc_target_amount: 0,
            under_production_amount: 0,
            under_production_target: 0
          }
          this.$buefy.toast.open({ message: 'No bonus variables found', duration: 3000, type: 'is-danger' })
        }
        const movements = await CrateMovements.getTable({
          start: this.start.toLocaleDateString('en-CA'),
          end: this.end.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id
        })
        movements.forEach(element => {
          this.movements.push({
            id: element.id,
            id_site: element.id_site,
            operation: element.operation,
            num_crates: element.num_crates,
            status_crate_end: element.status_crate_end,
            status_crate_start: element.status_crate_start,
            type: element.status_crate_end.split('__')[0],
            shift: element.shift,
            hours: element.hours,
            date: dayKey(element.date),
            comment: element.comment
          })
        })
        const schedules = await EmployeeSchedule.getTable({
          start: this.start.toLocaleDateString('en-CA'),
          end: this.end.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id
        })
        let washingMovements = this.movements.filter((x) => {
          return x.operation === 'washing'
        })
        let machineMovements = this.movements.filter((x) => {
          return x.operation === 'machineWash'
        })
        let repairMovements = this.movements.filter((x) => {
          return x.operation === 'repair'
        })
        let doorMovements = this.movements.filter((x) => {
          return x.operation === 'repair__door'
        })
        let forkliftMovements = this.movements.filter((x) => {
          return x.operation.split('__')[0] === 'forklift'
        })
        schedules.employees = schedules.employees.filter((x) => {
          var a = true
          var b = true
          if (x.date_entree) {
            if (!(dayKey(x.date_entree) <= this.end.toLocaleDateString('en-CA'))) {
              a = false
            }
          }
          if (x.date_sortie) {
            if (!(dayKey(x.date_sortie) >= this.start.toLocaleDateString('en-CA'))) {
              b = false
            }
          }
          return a && b
        })
        schedules.employees.forEach(element => {
          // days and hours worked
          let workedShifts = schedules.schedules.filter((x) => {
            return x.id_employee === element.id && x.activity.split('__')[0] !== 'absent' && x.activity.split('__')[0] !== 'comment'
          })
          let workedHours = workedShifts.length > 0 ? workedShifts.reduce((sum, element) => sum + parseFloat(element.hours ? element.hours : 0), 0) : 0
          let workedDays = [...new Set(workedShifts.map(workedDay => workedDay.date))].length
          // absence
          let absenceShifts = schedules.schedules.filter((x) => {
            return x.id_employee === element.id && x.activity.split('__')[0] === 'absent' && x.activity.split('__')[1] !== 'AL'
          })
          let absentDays = 0
          absenceShifts.forEach((shift) => {
            let day = schedules.schedules.filter((x) => {
              return x.id_employee === element.id && x.activity.split('__')[0] !== 'absent' && x.activity.split('__')[0] !== 'comment' && dayKey(x.date) === dayKey(shift.date)
            })
            if (day && day.length <= 0) {
              absentDays += 1
            }
          })
          // washing
          let washingShifts = schedules.schedules.filter((x) => {
            return x.id_employee === element.id && x.activity === 'washing'
          })
          let washingProd = []
          washingShifts.forEach((shift) => {
            let crateNumbers = washingMovements.filter((x) => {
              return dayKey(x.date) === dayKey(shift.date) && x.shift === shift.shift
            }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
            let participationTotal = schedules.schedules.filter((x) => {
              return dayKey(x.date) === dayKey(shift.date) && x.activity === 'washing' && x.shift === shift.shift
            }).reduce((sum, element) => sum + parseFloat(element.hours), 0)
            if (crateNumbers !== 0) {
              washingProd.push([(crateNumbers / participationTotal) * shift.hours, shift.hours])
            }
          })
          let washingTotal = 0
          let washingTotalHours = 0
          washingProd.forEach((element) => {
            washingTotal += element[0]
            washingTotalHours += element[1]
          })
          washingProd = washingTotalHours !== 0 ? washingTotal / washingTotalHours : 0
          // machine
          let machineShifts = schedules.schedules.filter((x) => {
            return x.id_employee === element.id && x.activity === 'machineWash'
          })
          let machineProd = []
          machineShifts.forEach((shift) => {
            let crateNumbers = machineMovements.filter((x) => {
              return dayKey(x.date) === dayKey(shift.date) && x.shift === shift.shift
            }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
            let participationTotal = schedules.schedules.filter((x) => {
              return dayKey(x.date) === dayKey(shift.date) && x.activity === 'machineWash' && x.shift === shift.shift
            }).reduce((sum, element) => sum + parseFloat(element.hours), 0)
            if (crateNumbers !== 0) {
              machineProd.push([(crateNumbers / participationTotal) * shift.hours, shift.hours])
            }
          })
          let machineTotal = 0
          let machineTotalHours = 0
          machineProd.forEach((element) => {
            machineTotal += element[0]
            machineTotalHours += element[1]
          })
          machineProd = machineTotalHours !== 0 ? machineTotal / machineTotalHours : 0
          // repair
          let repairShifts = schedules.schedules.filter((x) => {
            return x.id_employee === element.id && x.activity === 'repair'
          })
          let repairProd = []
          repairShifts.forEach((shift) => {
            let crateNumbers = repairMovements.filter((x) => {
              return dayKey(x.date) === dayKey(shift.date) && x.shift === shift.shift
            }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
            let participationTotal = schedules.schedules.filter((x) => {
              return dayKey(x.date) === dayKey(shift.date) && x.activity === 'repair' && x.shift === shift.shift
            }).reduce((sum, element) => sum + parseFloat(element.hours), 0)
            if (crateNumbers !== 0) {
              repairProd.push([(crateNumbers / participationTotal) * shift.hours, shift.hours])
            }
          })
          let repairTotal = 0
          let repairTotalHours = 0
          repairProd.forEach((element) => {
            repairTotal += element[0]
            repairTotalHours += element[1]
          })
          repairProd = repairTotalHours !== 0 ? repairTotal / repairTotalHours : 0
          // door
          let doorShifts = schedules.schedules.filter((x) => {
            return x.id_employee === element.id && x.activity === 'repair__door'
          })
          let doorProd = []
          doorShifts.forEach((shift) => {
            let crateNumbers = doorMovements.filter((x) => {
              return dayKey(x.date) === dayKey(shift.date) && x.shift === shift.shift
            }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
            let participationTotal = schedules.schedules.filter((x) => {
              return dayKey(x.date) === dayKey(shift.date) && x.activity === 'repair__door' && x.shift === shift.shift
            }).reduce((sum, element) => sum + parseFloat(element.hours), 0)
            if (crateNumbers !== 0) {
              doorProd.push([(crateNumbers / participationTotal) * shift.hours, shift.hours])
            }
          })
          let doorTotal = 0
          let doorTotalHours = 0
          doorProd.forEach((element) => {
            doorTotal += element[0]
            doorTotalHours += element[1]
          })
          doorProd = doorTotalHours !== 0 && doorTotal !== 0 ? doorTotal / doorTotalHours : 0
          // forklift
          let forkliftShifts = schedules.schedules.filter((x) => {
            return x.id_employee === element.id && x.activity === 'forklift'
          })
          let forkliftProd = []
          forkliftShifts.forEach((shift) => {
            let crateNumbers = forkliftMovements.filter((x) => {
              return dayKey(x.date) === dayKey(shift.date) && x.shift === shift.shift
            }).reduce((sum, element) => sum + parseInt(element.num_crates), 0)
            let participationTotal = schedules.schedules.filter((x) => {
              return dayKey(x.date) === dayKey(shift.date) && x.activity === 'forklift' && x.shift === shift.shift
            }).reduce((sum, element) => sum + parseFloat(element.hours), 0)
            if (crateNumbers !== 0) {
              forkliftProd.push([(crateNumbers / participationTotal) * shift.hours, shift.hours])
            }
          })
          let forkliftTotal = 0
          let forkliftTotalHours = 0
          forkliftProd.forEach((element) => {
            forkliftTotal += element[0]
            forkliftTotalHours += element[1]
          })
          forkliftProd = forkliftTotalHours !== 0 ? forkliftTotal / forkliftTotalHours : 0

          let mainActivity = 'none'
          let productionTarget = false
          let lowproductionTarget = false
          let qualityTarget = true
          if (this.ncs && this.ncs.length > 0) {
            qualityTarget = false
          }
          switch (Math.max(washingTotalHours, machineTotalHours, repairTotalHours, doorTotalHours, forkliftTotalHours)) {
            case 0:
              mainActivity = 'none'
              break
            case washingTotalHours:
              mainActivity = 'washing'
              if (washingProd >= this.bonus.hourly_washing) {
                productionTarget = true
              } else if (washingProd >= (this.bonus.hourly_washing * (this.bonus.under_production_target / 100))) {
                lowproductionTarget = true
              }
              break
            case machineTotalHours:
              mainActivity = 'machine'
              if (machineProd >= this.bonus.hourly_machinewash) {
                productionTarget = true
              } else if (machineProd >= (this.bonus.hourly_machinewash * (this.bonus.under_production_target / 100))) {
                lowproductionTarget = true
              }
              break
            case repairTotalHours:
              mainActivity = 'repair'
              if (repairProd >= this.bonus.hourly_repair) {
                productionTarget = true
              } else if (repairProd >= (this.bonus.hourly_repair * (this.bonus.under_production_target / 100))) {
                lowproductionTarget = true
              }
              break
            case doorTotalHours:
              mainActivity = 'repair__door'
              if (doorProd >= this.bonus.hourly_repair_door) {
                productionTarget = true
              } else if (doorProd >= (this.bonus.hourly_repair_door * (this.bonus.under_production_target / 100))) {
                lowproductionTarget = true
              }
              break
            case forkliftTotalHours:
              mainActivity = 'forklift'
              if (qualityTarget === true) {
                productionTarget = true
              }
              break
          }
          this.employees.push({
            id: element.id,
            id_site: element.id_site,
            name: element.name,
            matricule: element.matricule,
            title: element.title,
            surname: element.surname,
            date_entree: element.date_entree ? dayKey(element.date_entree) : null,
            date_sortie: element.date_sortie ? dayKey(element.date_sortie) : null,
            last_shift: element.lastShift,
            washing_productivity: washingProd,
            machine_productivity: machineProd,
            repair_productivity: repairProd,
            door_productivity: doorProd,
            forklift_productivity: forkliftProd,
            main_activity: mainActivity,
            production_target: productionTarget,
            lowproductionTarget: lowproductionTarget,
            quality_target: qualityTarget,
            absent_days: absentDays,
            workedDays: workedDays,
            workedHours: workedHours,
            absences: schedules.schedules.filter((x) => {
              return x.id_employee === element.id && x.activity.split('__')[0] === 'absent'
            })
          })
        })
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
        this.isLoading = false
      }
      this.storedPlantId = this.selectedPlant.id
      this.isLoaded = true
      this.isLoading = false
    },
    exportToExcel () {
      var lines = []
      var opts = {
        type: 'string'
      }
      lines.push(['Calculated bonus for plant', this.storedPlantId])
      lines.push(['Start of Period', this.savedStart, 'End of Period', this.savedEnd])
      lines.push([])
      lines.push(['Hourly Productivity Targets:'])
      lines.push(['Manual', 'Machine', 'Crate Repair', 'Door Repair', 'Minimum Production Percentage'])
      lines.push([this.bonus.hourly_washing, this.bonus.hourly_machinewash, this.bonus.hourly_repair, this.bonus.hourly_repair_door, this.bonus.under_production_target])
      lines.push([])
      lines.push(['Bonus Amounts'])
      lines.push(['Attendance Bonus', 'All Targets Met Bonus', 'Production Target Met Bonus', 'Minimum Production Target Met Bonus', 'Quality Target Met Bonus'])
      lines.push([this.bonus.attendance_amount, this.bonus.all_targets_amount, this.bonus.prod_target_amount, this.bonus.under_production_amount, this.bonus.nc_target_amount])
      lines.push([])
      lines.push(['Employee Performances'])
      lines.push(['Employee', 'Absences', 'Manual', 'Machine', 'Crate Repair', 'Door Repair', 'Forklift', 'Main Activity', 'Calculated Bonus', 'Attendance Bonus', 'Days Worked', 'Hours Worked', 'Medical Leave Days', 'Absenteeism Days', 'Annual Leave Days'])
      this.displayedEmployees.forEach((employee) => {
        lines.push([employee.name + (employee.surname && employee.surname.length > 0 ? ' ' + employee.surname : ''), employee.absent_days, employee.washing_productivity, employee.machine_productivity, employee.repair_productivity, employee.door_productivity, 'N/A', employee.main_activity, this.targetBonus(employee), this.attendanceBonus(employee), employee.workedDays, employee.workedHours, employee.absences.filter((x) => { return x.activity.split('__')[1] === 'ML' }).length, employee.absences.filter((x) => { return x.activity.split('__')[1] === 'AB' }).length, employee.absences.filter((x) => { return x.activity.split('__')[1] === 'AL' }).length])
      })
      var workbook = XLSX.utils.book_new()
      var worksheet = XLSX.utils.aoa_to_sheet(lines)
      worksheet['!cols'] = [
        { wch: 23 }
      ]
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Bonus')
      XLSX.writeFile(workbook, 'exportedData.xlsx', opts)
    }
  }
}

</script>
